// © 2026 AttainIP Engineering
import { Fragment, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchUser, fetchUserChats, fetchUserChat, fetchAnalysisChat, fetchJobStatus } from '@/api/queries';
import { api } from '@/api/client';
import { Card } from '@/components/Card';
import { ArrowLeft, ChevronRight, ChevronDown, X, MessageSquare } from 'lucide-react';
import { AuditEventDetail } from '@/components/AuditEventDetail';
import { TranscriptMessage } from '@/components/TranscriptMessage';

// Funnel stages derived from audit events
const FUNNEL_STAGES = [
  { key: 'auth.login_success', label: 'Logged in' },
  { key: 'user.activated', label: 'Activated' },
  { key: 'credit.purchased', label: 'Purchased credits' },
  { key: 'analysis.submitted', label: 'Submitted analysis' },
  { key: 'analysis.completed', label: 'Analysis completed' },
  { key: 'report.accessed', label: 'Viewed report' },
  { key: 'feedback.submitted', label: 'Gave feedback' },
  { key: 'chat.message_sent', label: 'Used interrogation' },
];

const PLATFORM_ADMIN_DOMAIN = '@attainip.ai';

function RoleToggle({
  userId,
  email,
  currentRoles,
}: {
  userId: string;
  email: string | undefined;
  currentRoles: string[];
}) {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const isAdmin = currentRoles.includes('platform_admin');
  const isAttainipUser = (email || '').toLowerCase().endsWith(PLATFORM_ADMIN_DOMAIN);
  // Grant is gated by email domain (RFC-0121 A1); revoke is unrestricted.
  const grantBlocked = !isAdmin && !isAttainipUser;

  const handleToggle = async () => {
    if (grantBlocked) return;
    setLoading(true);
    try {
      const newRoles = isAdmin
        ? currentRoles.filter((r) => r !== 'platform_admin')
        : [...currentRoles, 'platform_admin'];
      await api.patch(`/v1/users/${userId}/roles`, { roles: newRoles });
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const code = detail?.code ?? detail?.error;
      const message = detail?.message ?? (typeof detail === 'string' ? detail : null);
      if (code === 'platform_admin_grant_forbidden' || err.response?.status === 403) {
        alert(message || 'platform_admin can only be granted to @attainip.ai users');
      } else {
        alert(`Failed to update roles${message ? `: ${message}` : ''}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const baseClass = `text-xs px-2 py-0.5 rounded border disabled:opacity-50 ${
    isAdmin
      ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
      : 'bg-green-50 text-green-600 border-green-200 hover:bg-green-100'
  }`;

  return (
    <button
      onClick={handleToggle}
      disabled={loading || grantBlocked}
      title={grantBlocked ? 'Only @attainip.ai users can be platform admins.' : undefined}
      className={baseClass}
    >
      {loading ? '...' : isAdmin ? 'Remove admin' : 'Make admin'}
    </button>
  );
}

function CreditAdjustForm({ userId }: { userId: string }) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const num = parseInt(amount, 10);
    if (isNaN(num) || num === 0 || !description.trim()) return;

    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post(`/v1/users/${userId}/credits`, { amount: num, description: description.trim() });
      setResult(`${num > 0 ? '+' : ''}${num} credits applied. New balance: ${data.new_balance}`);
      setAmount('');
      setDescription('');
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    } catch (err: any) {
      setResult(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <div className="flex gap-2">
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Amount (e.g. 100 or -50)"
          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Reason"
          className="flex-2 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          disabled={loading || !amount || !description.trim()}
          className="px-3 py-1.5 bg-gray-900 text-white rounded text-sm hover:bg-gray-800 disabled:opacity-50"
        >
          {loading ? '...' : 'Apply'}
        </button>
      </div>
      {result && <p className={`text-xs ${result.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>{result}</p>}
    </form>
  );
}

function ChatTranscriptDrawer({
  userId,
  userEmail,
  sessionId,
  sessionTitle,
  onClose,
}: {
  userId: string;
  userEmail?: string;
  sessionId: string;
  sessionTitle: string;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-chat', userId, sessionId],
    queryFn: () => fetchUserChat(userId, sessionId),
  });

  const messages: any[] = data?.messages || [];

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/30" />
      <div
        className="w-full max-w-3xl h-full bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b px-4 py-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-semibold truncate">{sessionTitle}</h2>
            <p className="text-xs text-gray-500 truncate">{userEmail || userId}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-4 py-2 bg-yellow-50 border-b text-xs text-yellow-800">
          Read-only — every view is audited.
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {isLoading && <p className="text-sm text-gray-400">Loading transcript…</p>}
          {error && <p className="text-sm text-red-600">Failed to load transcript</p>}
          {!isLoading && !error && messages.length === 0 && (
            <p className="text-sm text-gray-400">No messages in this session.</p>
          )}
          <div className="space-y-4">
            {messages.map((m: any, i: number) => (
              <TranscriptMessage key={m.id ?? `${i}-${m.role}`} raw={m} index={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AnalysisChatDrawer({
  analysisId,
  analysisLabel,
  userEmail,
  onClose,
}: {
  analysisId: string;
  analysisLabel: string;
  userEmail?: string;
  onClose: () => void;
}) {
  // Step 1 — kick off the Engine chat-history job. This is a fast call
  // (<1s) that returns { status: "pending", job_id, user_email } or
  // { status: "done", messages } for the rare synchronous-Engine path.
  const kickoff = useQuery({
    queryKey: ['analysis-chat-kickoff', analysisId, userEmail],
    queryFn: () => fetchAnalysisChat(analysisId, userEmail),
    retry: false,
    // gcTime: 0 so reopening the drawer always issues a fresh kickoff
    gcTime: 0,
  });

  const jobId: string | null =
    kickoff.data?.status === 'pending' ? kickoff.data?.job_id || null : null;
  const ownerEmail: string | undefined = kickoff.data?.user_email || userEmail;

  // Step 2 — poll the job-status endpoint on a 1s interval until it
  // resolves to done/error. Each call is fast and individually proxy-safe.
  const poll = useQuery({
    queryKey: ['analysis-chat-poll', jobId, ownerEmail],
    queryFn: () => fetchJobStatus(jobId!, ownerEmail!, 'PROCESS'),
    enabled: Boolean(jobId && ownerEmail),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'done' || s === 'error' ? false : 1000;
    },
    retry: false,
    gcTime: 0,
  });

  // Coalesce the two query states.
  const isLoading =
    kickoff.isLoading ||
    (jobId !== null && poll.data?.status !== 'done' && poll.data?.status !== 'error');
  const errorPayload =
    kickoff.error ||
    poll.error ||
    (poll.data?.status === 'error' ? { message: poll.data.message } : null);
  const messages: any[] =
    kickoff.data?.status === 'done'
      ? kickoff.data.messages || []
      : poll.data?.status === 'done'
      ? poll.data.messages || []
      : [];
  const progressMessage =
    poll.data && poll.data.status !== 'done' && poll.data.status !== 'error'
      ? poll.data.message || null
      : null;

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/30" />
      <div
        className="w-full max-w-3xl h-full bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b px-4 py-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-semibold truncate">Interrogation Chat</h2>
            <p className="text-xs text-gray-500 truncate font-mono">{analysisLabel}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-4 py-2 bg-yellow-50 border-b text-xs text-yellow-800">
          Read-only — sourced from the Attain Engine. Polled until the chat-history job completes.
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {isLoading && (
            <p className="text-sm text-gray-400">
              Loading transcript…{progressMessage ? ` ${progressMessage}` : ''}
            </p>
          )}
          {errorPayload && (
            <p className="text-sm text-red-600">
              Failed to load chat:{' '}
              {(errorPayload as any)?.response?.data?.detail ||
                (errorPayload as any)?.message ||
                String(errorPayload)}
            </p>
          )}
          {!isLoading && !errorPayload && messages.length === 0 && (
            <p className="text-sm text-gray-400">No chat messages for this analysis.</p>
          )}
          <div className="space-y-4">
            {messages.map((m: any, i: number) => (
              <TranscriptMessage key={m.id ?? `${i}-${m.role}`} raw={m} index={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function UserChats({ userId, userEmail }: { userId: string; userEmail?: string }) {
  const [open, setOpen] = useState<{ id: string; title: string } | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-chats', userId],
    queryFn: () => fetchUserChats(userId),
  });

  const chats: any[] = useMemo(() => {
    const raw = data?.chats || [];
    // Sort newest first by last activity, falling back to created_at.
    // Core Services doesn't guarantee order, so we own this client-side.
    const timestamp = (c: any): number => {
      const t = c.last_message_at || c.updated_at || c.created_at;
      const ms = t ? Date.parse(t) : NaN;
      return Number.isFinite(ms) ? ms : 0;
    };
    return [...raw].sort((a, b) => timestamp(b) - timestamp(a));
  }, [data]);

  return (
    <Card title={`Assistant Chats (${chats.length})`}>
      {isLoading && <p className="text-sm text-gray-400">Loading chats…</p>}
      {error && <p className="text-sm text-red-600">Failed to load chats</p>}
      {!isLoading && !error && chats.length === 0 && (
        <p className="text-sm text-gray-400">No Assistant chats for this user.</p>
      )}
      {!isLoading && !error && chats.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 font-medium">Title</th>
              <th className="pb-2 font-medium">Messages</th>
              <th className="pb-2 font-medium">Last activity ↓</th>
              <th className="pb-2 font-medium">Created</th>
              <th className="pb-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {chats.map((c: any) => {
              const id = c.chat_id || c.session_id || c.id;
              const title = c.title || c.chat_name || `Chat ${String(id).slice(0, 8)}…`;
              const created = c.created_at;
              const last = c.last_message_at || c.updated_at;
              const count = c.message_count;
              return (
                <tr
                  key={id}
                  className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setOpen({ id, title })}
                >
                  <td className="py-2 truncate max-w-md">{title}</td>
                  <td className="py-2 text-xs text-gray-600">{count ?? '—'}</td>
                  <td className="py-2 text-xs text-gray-400">
                    {last ? new Date(last).toLocaleString() : '—'}
                  </td>
                  <td className="py-2 text-xs text-gray-400">
                    {created ? new Date(created).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-2 text-right">
                    <MessageSquare size={14} className="text-gray-400 inline" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {open && (
        <ChatTranscriptDrawer
          userId={userId}
          userEmail={userEmail}
          sessionId={open.id}
          sessionTitle={open.title}
          onClose={() => setOpen(null)}
        />
      )}
    </Card>
  );
}

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const { data, isLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => fetchUser(userId!),
    enabled: !!userId,
  });

  const profile = data?.profile || {};
  const ssProfile = data?.ss_profile || {};
  const credits = data?.credits || {};
  const creditTxns = credits.transactions || [];
  const analyses = data?.analyses?.data || data?.analyses || [];
  const feedback = data?.feedback?.data || data?.feedback || [];
  const events = data?.recent_events?.data || data?.recent_events || [];
  const [expandedEvents, setExpandedEvents] = useState<Record<string, boolean>>({});
  const [openAnalysisChat, setOpenAnalysisChat] = useState<{ id: string; label: string } | null>(null);

  // Group events by type for summary
  const eventCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    if (Array.isArray(events)) {
      events.forEach((evt: any) => {
        const key = evt.event || 'unknown';
        counts[key] = (counts[key] || 0) + 1;
      });
    }
    return counts;
  }, [events]);

  if (isLoading) return <p className="text-gray-500">Loading user...</p>;
  if (!data) return <p className="text-red-600">User not found</p>;

  // First and last seen
  const firstSeen = profile.created_at ? new Date(profile.created_at).toLocaleDateString() : '—';
  const lastEvent = Array.isArray(events) && events.length > 0 ? events[0] : null;
  const lastSeen = lastEvent?.timestamp ? new Date(lastEvent.timestamp).toLocaleString() : '—';

  return (
    <div>
      <Link to="/users" className="text-sm text-blue-600 hover:underline flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Back to users
      </Link>

      <h1 className="text-xl font-bold mb-6">{profile.display_name || profile.email || 'User Detail'}</h1>

      {/* Row 1: Profile + Account Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card title="Profile">
          <dl className="space-y-2 text-sm">
            <div className="flex"><dt className="w-28 text-gray-500">ID</dt><dd className="font-mono text-xs">{profile.id}</dd></div>
            <div className="flex"><dt className="w-28 text-gray-500">Email</dt><dd>{profile.email}</dd></div>
            <div className="flex"><dt className="w-28 text-gray-500">Name</dt><dd>{profile.display_name || '—'}</dd></div>
            <div className="flex"><dt className="w-28 text-gray-500">Org ID</dt><dd className="font-mono text-xs">{profile.organization_id || '—'}</dd></div>
            <div className="flex items-center">
              <dt className="w-28 text-gray-500">Roles</dt>
              <dd className="flex items-center gap-2">
                <span>{(profile.roles || []).join(', ') || '—'}</span>
                {profile.id && <RoleToggle userId={userId!} email={profile.email} currentRoles={profile.roles || []} />}
              </dd>
            </div>
            <div className="flex"><dt className="w-28 text-gray-500">Audiences</dt><dd>{(profile.audiences || []).join(', ') || '—'}</dd></div>
            <div className="flex">
              <dt className="w-28 text-gray-500">Status</dt>
              <dd>
                <span className={`text-xs px-2 py-0.5 rounded ${profile.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {profile.status || 'unknown'}
                </span>
              </dd>
            </div>
            <div className="flex"><dt className="w-28 text-gray-500">Credits</dt><dd className="font-bold">{credits.balance ?? ssProfile.credit_balance ?? '—'}</dd></div>
            <div className="flex"><dt className="w-28 text-gray-500">Referral code</dt><dd className="font-mono text-xs">{ssProfile.referral_code || '—'}</dd></div>
            <div className="flex"><dt className="w-28 text-gray-500">Activated</dt><dd className="text-xs">{ssProfile.activated_at ? new Date(ssProfile.activated_at).toLocaleDateString() : 'Not activated'}</dd></div>
            <div className="flex"><dt className="w-28 text-gray-500">First seen</dt><dd className="text-xs">{firstSeen}</dd></div>
            <div className="flex"><dt className="w-28 text-gray-500">Last active</dt><dd className="text-xs">{lastSeen}</dd></div>
          </dl>
        </Card>

        <Card title="User Funnel">
          <div className="space-y-1">
            {FUNNEL_STAGES.map(({ key, label }) => {
              const count = eventCounts[key] || 0;
              const reached = count > 0;
              return (
                <div key={key} className="flex items-center gap-3 py-1">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${reached ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                    {reached ? '✓' : '·'}
                  </span>
                  <span className={`text-sm flex-1 ${reached ? 'text-gray-900' : 'text-gray-400'}`}>{label}</span>
                  {count > 0 && <span className="text-xs text-gray-400">×{count}</span>}
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Row 2: Event Summary + Feedback */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card title="Event Summary">
          {Object.keys(eventCounts).length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="pb-2 font-medium">Event</th>
                  <th className="pb-2 font-medium text-right">Count</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(eventCounts)
                  .sort(([, a], [, b]) => b - a)
                  .map(([event, count]) => (
                    <tr key={event} className="border-b last:border-0">
                      <td className="py-1 font-mono text-xs">{event}</td>
                      <td className="py-1 text-right text-gray-500">{count}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-400 text-sm">No events recorded</p>
          )}
        </Card>

        <Card title={`Feedback (${Array.isArray(feedback) ? feedback.length : 0})`}>
          {Array.isArray(feedback) && feedback.length > 0 ? (
            <div className="space-y-2">
              {feedback.map((fb: any) => (
                <div key={fb.id} className="text-sm border-b pb-2 last:border-0">
                  <div className="flex justify-between">
                    <span className="text-yellow-500">{'★'.repeat(fb.rating)}{'☆'.repeat(5 - fb.rating)}</span>
                    <span className="text-xs text-gray-400">{new Date(fb.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-gray-600 text-xs mt-1">{fb.comment}</p>
                  {fb.resource_id && <p className="text-gray-400 text-xs mt-0.5 font-mono">analysis: {fb.resource_id}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No feedback</p>
          )}
        </Card>
      </div>

      {/* Row 3: Analyses + Credits */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card title={`Analyses (${Array.isArray(analyses) ? analyses.length : 0})`}>
          {Array.isArray(analyses) && analyses.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="pb-2 font-medium">ID</th>
                  <th className="pb-2 font-medium">Case</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium">Created</th>
                  <th className="pb-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {analyses.map((a: any) => {
                  const id = a.analysis_id || a.id;
                  // Some shapes carry `case_id`+`work_item_id` separately
                  // (the merged advanced rows); prefer the canonical
                  // `case-N-wi-N` ID for those since the SS API will then
                  // accept it directly. Fall back to the short ID for
                  // simple analyses.
                  const chatId = a.case_id && a.work_item_id
                    ? `case-${a.case_id}-wi-${a.work_item_id}`
                    : id;
                  return (
                    <tr key={id} className="border-b last:border-0">
                      <td className="py-2 font-mono text-xs">{id}</td>
                      <td className="py-2 text-xs text-gray-500 truncate max-w-40">{a.case_name || '—'}</td>
                      <td className="py-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          a.status === 'complete' || a.status === 'completed' ? 'bg-green-100 text-green-700' :
                          a.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {a.status || '—'}
                        </span>
                      </td>
                      <td className="py-2 text-xs text-gray-400">{a.created_at ? new Date(a.created_at).toLocaleString() : '—'}</td>
                      <td className="py-2 text-right">
                        <button
                          onClick={() => setOpenAnalysisChat({ id: chatId, label: a.case_name ? `${a.case_name} · ${chatId}` : chatId })}
                          className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                          title="View interrogation chat"
                        >
                          <MessageSquare size={12} /> Chat
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-400 text-sm">No analyses</p>
          )}
        </Card>

        <Card title="Adjust Credits">
          <CreditAdjustForm userId={userId!} />
        </Card>

        <Card title={`Credit Transactions (${creditTxns.length})`}>
          {creditTxns.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="pb-2 font-medium">Type</th>
                  <th className="pb-2 font-medium text-right">Amount</th>
                  <th className="pb-2 font-medium text-right">Balance</th>
                  <th className="pb-2 font-medium">Description</th>
                  <th className="pb-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {creditTxns.map((tx: any) => {
                  // The API stores `amount` as a positive integer; direction
                  // is implied by `transaction_type`. Mirror PaymentsPage's
                  // DEBIT_TYPES set so analysis/admin_deduct/expiry render
                  // with a minus sign and red colour.
                  const isDebit = ['analysis', 'admin_deduct', 'expiry'].includes(tx.transaction_type);
                  const signed = `${isDebit ? '-' : '+'}${Math.abs(tx.amount)}`;
                  return (
                    <tr key={tx.id} className="border-b last:border-0">
                      <td className="py-1 text-xs">{tx.transaction_type}</td>
                      <td className={`py-1 text-xs text-right font-mono ${isDebit ? 'text-red-600' : 'text-green-600'}`}>
                        {signed}
                      </td>
                      <td className="py-1 text-xs text-right font-mono">{tx.balance_after}</td>
                      <td className="py-1 text-xs text-gray-500 truncate max-w-32">{tx.description || '—'}</td>
                      <td className="py-1 text-xs text-gray-400">{tx.created_at ? new Date(tx.created_at).toLocaleString() : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-400 text-sm">No credit transactions</p>
          )}
        </Card>
      </div>

      {/* Row 4: Assistant Chats (read-only, audited — RFC-0212 A1) */}
      <div className="mb-4">
        <UserChats userId={userId!} userEmail={profile.email} />
      </div>

      {/* Row 5: Full Activity Log */}
      <Card title={`Activity Log (${Array.isArray(events) ? events.length : 0})`}>
        {Array.isArray(events) && events.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="pb-2 font-medium w-6"></th>
                <th className="pb-2 font-medium">Event</th>
                <th className="pb-2 font-medium">Resource</th>
                <th className="pb-2 font-medium">Outcome</th>
                <th className="pb-2 font-medium">Time</th>
              </tr>
            </thead>
            <tbody>
              {events.map((evt: any) => {
                const isOpen = !!expandedEvents[evt.id];
                const hasDetail =
                  (evt.metadata && Object.keys(evt.metadata).length > 0) ||
                  evt.correlation_id || evt.idempotency_key;
                const toggle = () =>
                  setExpandedEvents((s) => ({ ...s, [evt.id]: !s[evt.id] }));
                return (
                  <Fragment key={evt.id}>
                    <tr
                      className={`border-b last:border-0 hover:bg-gray-50 ${hasDetail ? 'cursor-pointer' : ''}`}
                      onClick={hasDetail ? toggle : undefined}
                    >
                      <td className="py-1 text-gray-400">
                        {hasDetail ? (
                          isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />
                        ) : null}
                      </td>
                      <td className="py-1 font-mono text-xs">{evt.event || '—'}</td>
                      <td className="py-1 text-xs text-gray-400">{evt.resource_type ? `${evt.resource_type}${evt.resource_id ? '/' + evt.resource_id : ''}` : (evt.metadata?.session_id ? `session/${evt.metadata.session_id}` : '—')}</td>
                      <td className="py-1">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${evt.outcome === 'success' ? 'bg-green-100 text-green-700' : evt.outcome === 'failure' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                          {evt.outcome || '—'}
                        </span>
                      </td>
                      <td className="py-1 text-xs text-gray-400">{evt.timestamp ? new Date(evt.timestamp).toLocaleString() : '—'}</td>
                    </tr>
                    {isOpen && hasDetail && (
                      <tr className="border-b last:border-0 bg-gray-50">
                        <td></td>
                        <td colSpan={4} className="py-3 pr-4">
                          <AuditEventDetail evt={evt} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-400 text-sm">No activity recorded</p>
        )}
      </Card>

      {openAnalysisChat && (
        <AnalysisChatDrawer
          analysisId={openAnalysisChat.id}
          analysisLabel={openAnalysisChat.label}
          userEmail={profile.email}
          onClose={() => setOpenAnalysisChat(null)}
        />
      )}
    </div>
  );
}
